import {FilterType} from '../enums/core/filter-type';

export class ArrayHelper {

  /**
   *
   * @param list
   * @param item
   * @param key
   */
  public static update<T extends { uuid?: string }>(list: T[], item: T, key: keyof T = 'uuid'): T[] {
    return list.map(_list => _list[key] === item[key] ? item : _list);
  }

  /**
   *
   * @param list
   * @param item
   * @param key
   * @param mode
   */
  public static add<T extends { uuid?: string }>(list: T[], item: T, key: keyof T = 'uuid', mode: 'PUSH' | 'UNSHIFT' = 'PUSH'): T[] {
    return mode === 'PUSH' ? [...list, item] : [item, ...list];
  }

  /**
   *
   * @param list
   * @param item
   * @param key
   * @param mode
   */
  public static addOrUpdate<T extends { uuid?: string }>(list: T[], item: T, key: keyof T = 'uuid', mode: 'PUSH' | 'UNSHIFT' = 'PUSH'): T[] {
    const searchedList = list.find(_list => _list[key] === item[key]);
    if(searchedList) {
      return ArrayHelper.update(list, item, key);
    }
    return ArrayHelper.add(list, item, key, mode);
  }

  /**
   *
   * @param list
   * @param item
   * @param key
   */
  public static remove<T extends { uuid?: string }>(list: T[], item: T | string, key: keyof T = 'uuid'): T[] {
    if(typeof(item) === 'string') {
      return list.filter(_list => _list[key] !== item);
    }

    return list.filter(_list => _list[key] !== item[key]);
  }

  /**
   *
   */
  public static sort<T>(list: T[], keys: { key: string, order?: 'ASC' | 'DESC' }[]): T[] {
    // Create a copy to avoid modifying the original array
    return list.slice().sort((a, b) => {
      for (const { key, order = 'ASC' } of keys) {
        const keysPath = key.split('.');
        const valueA = ArrayHelper.getValue(a, keysPath);
        const valueB = ArrayHelper.getValue(b, keysPath);

        if (valueA > valueB) {
          return order === 'ASC' ? 1 : -1;
        }
        if (valueA < valueB) {
          return order === 'ASC' ? -1 : 1;
        }
        // If the values are equal, compare the next key
      }
      return 0;
    });
  }

  public static filter<T>(list: T[], key: string, query: string|number|boolean = null): T[] {

    let filterType = FilterType.Equals;
    if(typeof(query) === 'string') {
      filterType = FilterType.Includes;
    }

    if(query === null) {
      return list;
    }

    return list.filter(item => {
      const keys = key.split('.');
      const value = ArrayHelper.getValue(item, keys);

      return ArrayHelper.compare(value, query, filterType);
    });
  }


  /**
   *
   * @param list
   * @param item
   * @param key
   */
  public static find<T extends { id?: number }>(list: T[], item: T | number, key: keyof T = 'id'): T {
    const compareValue = typeof(item) === 'number' ? item : item[key];
    return list.find(_item => _item[key] === compareValue);
  }

  /**
   *
   * @param list
   * @param item
   * @param key
   */
  public static inArray<T extends { id?: number }>(list: T[], item: T | number, key: keyof T = 'id'): boolean {
    return !!ArrayHelper.find(list, item, key);
  }

  /**
   *
   * @param list1
   * @param list2
   * @param key
   */
  public static mergeUnique<T extends { id?: number }>(list1: T[], list2: T[], key: keyof T = 'id'): T[] {
    for(const item of list2) {
      if(!ArrayHelper.inArray(list1, item, key)) {
        list1 = [...list1, item];
      }
    }
    return list1;
  }


  /**
   *
   * @param list
   * @param key
   */
  public static unique<T extends { id?: number }>(list: T[], key: keyof T = 'id'): T[] {
    return list.reduce((_list: T[], currentItem: T) => {
      if(!_list.some(_item => _item[key] === currentItem[key])) {
        _list.push(currentItem);
      }
      return _list;
    }, []);
  }

  /**
   *
   * @param items
   * @param item
   * @param key
   */
  public static some<T extends { id?: number }>(items: T[], item: T | number, key: keyof T = 'id'): boolean {
    if(typeof(item) === 'number') {
      return items.some(_item => _item[key] === item);
    }
    return items.some(_item => _item[key] === item[key]);
  }

  /**
   *
   * @param item
   * @param keys
   * @private
   */
  private static getValue(item: any, keys: string[]): any {
    let value = item;
    for(const key of keys) {
      value = value[key];
    }
    return value;
  }

  private static compare(a: any, b: any, filterType: FilterType): any {
    if(filterType === FilterType.Equals) {
      return a === b;
    }
    if(filterType === FilterType.Includes) {
      return a.toString().toLowerCase().includes(b.toString().toLowerCase());
    }
    if(filterType === FilterType.StartsWith) {
      return a.toString().toLowerCase().startsWith(b.toString().toLowerCase());
    }
    return false;
  }

}
